import "./DCReporting.css";
import { useState } from "react";
import React from "react";
import { Button } from "../../../ds2/components";
import { HEADER_LABELS } from "./dc-reporting-data";
import { AIResponseReportingMessage } from "winslow-core";
import {
  FileSpreadsheet,
  Eye,
  EyeOff,
  Maximize2,
  Download,
  Pin,
  PinOff,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLastViewedMessageId,
  selectUser,
} from "../../../redux/features/appData";
import { ReportViewer } from "../../wazawan/report-viewer/ReportViewer";
import { apiToggleMessagePin } from "../../../services/AIService";
import { updateSpecificMessage } from "../../../redux/features/agent/agentSlice";
import toast from "react-hot-toast";

type DCReportingProps = {
  message?: AIResponseReportingMessage;
  data: any;
  isPreview: boolean;
  forceExpanded?: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
};

export const DCReporting = ({
  message,
  data,
  isPreview,
  forceExpanded = false,
  onCancel,
  onSubmit,
}: DCReportingProps) => {
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useDispatch();
  const storeUser = useSelector(selectUser);

  const handleTogglePin = async () => {
    if (!storeUser?.id || !message) return;

    try {
      const updatedMessage = await apiToggleMessagePin(
        storeUser.id,
        message.id
      );
      if (updatedMessage) {
        dispatch(updateSpecificMessage(updatedMessage));
        toast.success(message.pinned ? "Message unpinned" : "Message pinned");
      }
    } catch (error) {
      console.error("Error toggling pin:", error);
      toast.error("Failed to toggle pin");
    }
  };

  const showLabel = (objectKey: string, propertyKey: string) => {
    if (objectKey.toLowerCase() === "employee-record") {
      switch (propertyKey.toLowerCase()) {
        case "id":
          return false;
      }
    }

    return true;
  };

  const getLabel = (objectKey: string, propertyKey: string) => {
    if (Object.keys(HEADER_LABELS).includes(objectKey.toLowerCase())) {
      const labels =
        HEADER_LABELS[objectKey.toLowerCase() as keyof typeof HEADER_LABELS];

      if (Object.keys(labels).includes(propertyKey.toLowerCase())) {
        return labels[propertyKey.toLowerCase() as keyof typeof labels];
      }
    }

    return propertyKey;
  };

  const getDataType = (data: any): "value" | "object" | "array" => {
    if (typeof data !== "object") {
      return "value";
    }

    if (Array.isArray(data)) {
      return "array";
    }

    // if (Object.keys(data).length === 2 && Array.isArray(data[Object.keys(data)[0]])) {
    //     return "array";
    // }

    return "object";
  };

  console.log("[DCReporting] data: ", data);

  // Handle the new reporting data structure
  let parsedData: any = {};
  let explanation = "";
  let reportData: any[] = [];

  // First check if we have a message object with the explanation
  if (message && message.message && message.message.explanation) {
    explanation = message.message.explanation;
    reportData = message.message.reportData || [];
  } else {
    // Fallback to parsing data directly
    try {
      parsedData = JSON.parse(data);
    } catch (e) {
      parsedData = data;
    }

    // Check if this is the new reporting format
    if (
      parsedData &&
      typeof parsedData === "object" &&
      parsedData.explanation &&
      parsedData.reportData
    ) {
      explanation = parsedData.explanation;
      reportData = Array.isArray(parsedData.reportData)
        ? parsedData.reportData
        : [];
    } else {
      // Fallback to old format
      explanation = parsedData?.commentary || "";
      reportData = Array.isArray(parsedData) ? parsedData : [parsedData];
    }
  }

  console.log("[DCReporting] parsedData: ", parsedData);
  console.log("[DCReporting] explanation: ", explanation);
  console.log("[DCReporting] reportData: ", reportData);
  console.log("[DCReporting] reportData typeof: ", typeof reportData);
  console.log("[DCReporting] reportData array?: ", Array.isArray(reportData));

  const dataType = getDataType(reportData);

  console.log("[DCReporting] dataType: ", dataType);
  let tableKey = undefined;

  if (dataType === "array") {
    tableKey = "employee-report";
  }
  if (dataType === "object") {
    tableKey = "employee-record";
  }

  const columns: string[] = [];
  const rowData: Map<string, any>[] = [];

  if (dataType === "object" && tableKey) {
    Object.keys(reportData).forEach((key) => {
      columns.push(key);
    });

    const rowMap = new Map<string, any>();
    Object.keys(reportData).forEach((key) => {
      rowMap.set(key, reportData[key as keyof typeof reportData]);
    });
    rowData.push(rowMap);
  }

  if (dataType === "array" && tableKey) {
    const dataArray = reportData as any[];

    // make column from keys of first object - assume uniformity
    if (dataArray.length > 0) {
      Object.keys(dataArray[0]).forEach((key) => {
        columns.push(key);
      });
    } else {
      console.log("[DCReporting] Empty array - no columns to extract");
    }

    // make rows from array of objects
    dataArray.forEach((row: any) => {
      const rowMap = new Map<string, any>();
      Object.keys(row).forEach((key) => {
        rowMap.set(key, row[key]);
      });
      rowData.push(rowMap);
    });
  }

  const handlePreview = () => {
    setIsPreviewExpanded(!isPreviewExpanded);
  };

  const handleExpand = () => {
    setIsExpanded(true);
    if (message) {
      dispatch(setLastViewedMessageId(message.id));
    }
  };

  const handleCancelExpand = () => {
    setIsExpanded(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleExport = () => {
    // Export report data as CSV
    if (reportData && Array.isArray(reportData) && reportData.length > 0) {
      const csvContent = convertToCSV(reportData, columns);
      downloadCSV(csvContent, "report-data.csv");
    }
  };

  const convertToCSV = (data: any[], cols: string[]): string => {
    const headers = cols
      .filter((col) => showLabel("employee-record", col))
      .map((col) => getLabel("employee-record", col))
      .join(",");

    const rows = data.map((row) =>
      cols
        .filter((col) => showLabel("employee-record", col))
        .map((col) => {
          let value = row[col] || "";
          if (typeof value === "object") {
            value = JSON.stringify(value);
          }
          // Escape quotes and wrap in quotes if contains comma
          return value.toString().includes(",")
            ? `"${value.toString().replace(/"/g, '""')}"`
            : value;
        })
        .join(",")
    );

    return [headers, ...rows].join("\n");
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Render expanded view using ReportViewer component
  if (isExpanded || forceExpanded) {
    return (
      <ReportViewer
        message={message}
        explanation={explanation}
        reportData={reportData}
        columns={columns}
        showLabel={showLabel}
        getLabel={getLabel}
        onCancel={handleCancelExpand}
        onExport={handleExport}
      />
    );
  }

  return (
    <div className={`dc-reporting ${isPreview ? "dc-reporting-preview" : ""}`}>
      {/* Pin Icon */}
      {message && 
        <Button
          variant="tertiary"
          size="sm"
          onClick={handleTogglePin}
          title={message.pinned ? "Unpin message" : "Pin message"}
        >
            {message.pinned ? <Pin size={12} /> : <PinOff size={12} />}
        </Button>
        
        // {/* <button
        //   className={`pin-button ${message.pinned ? "pinned" : ""}`}
        //   onClick={handleTogglePin}
        //   title={message.pinned ? "Unpin message" : "Pin message"}>
        //   {message.pinned ? <Pin size={12} /> : <PinOff size={12} />}
        // </button> */}
      }

      {/* Header section similar to DCAsset */}
      {(dataType === "array" || dataType === "object") && (
        <div className="report-header">
          <div className="report-header-top">
            <div className="report-info">
              <FileSpreadsheet className="report-icon" size={24} />
              <span className="report-type-label">REPORT</span>
              <div className="report-details">
                <h3 className="report-title">
                  {explanation ? "Employee Report" : "Data Report"}
                </h3>
              </div>
            </div>

            <div className="report-actions">
              {/* Only show Preview button when more than 5 rows */}
              {reportData &&
                Array.isArray(reportData) &&
                reportData.length > 5 && (
                  <Button
                    variant={isPreviewExpanded ? "primary" : "secondary"}
                    size="sm"
                    onClick={handlePreview}
                    title={isPreviewExpanded ? "Hide preview" : "Preview data"}
                    leftIcon={isPreviewExpanded ? <EyeOff size={16} /> : <Eye size={16} />}>
                    {isPreviewExpanded ? "Hide" : "Preview"}
                  </Button>
                  
                  // {/* <button
                  //   className={`action-button preview-button ${isPreviewExpanded ? "active" : ""}`}
                  //   onClick={handlePreview}
                  //   title={isPreviewExpanded ? "Hide preview" : "Preview data"}>
                  //   {isPreviewExpanded ? (
                  //     <EyeOff size={16} />
                  //   ) : (
                  //     <Eye size={16} />
                  //   )}
                  //   <span>{isPreviewExpanded ? "Hide" : "Preview"}</span>
                  // </button> */}
                )}

              {reportData &&
                Array.isArray(reportData) &&
                reportData.length > 0 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleExpand}
                    title="Expand to full view"
                    leftIcon={<Maximize2 size={16} />}>
                    Expand
                  </Button>
                  
                  // {/* <button
                  //   className="action-button expand-button"
                  //   onClick={handleExpand}
                  //   title="Expand to full view">
                  //   <Maximize2 size={16} />
                  //   <span>Expand</span>
                  // </button> */}
                )}

              {reportData &&
                Array.isArray(reportData) &&
                reportData.length > 0 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleExport}
                    title="Export as CSV"
                    leftIcon={<Download size={16} />}>
                    Export
                  </Button>
                  
                  // {/* <button
                  //   className="action-button download-button"
                  //   onClick={handleExport}
                  //   title="Export as CSV">
                  //   <Download size={16} />
                  //   <span>Export</span>
                  // </button> */}
                )}
            </div>
          </div>
          <div className="report-header-bottom">
            {reportData && Array.isArray(reportData) && (
              <span className="report-count">
                {reportData.length} record{reportData.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Explanation section */}
      {/* {explanation && (
        <div className="explanation-section">
          <span className="explanation-text">{explanation}</span>
        </div>
      )} */}

      {!tableKey && dataType === "value" && (
        <div className="dc-reporting-empty">{data}</div>
      )}

      {(dataType === "array" || dataType === "object") && (
        <div
          className={
            isPreview ? "dc-reporting-body-preview" : "dc-reporting-body"
          }>
          {/* Show empty state when no data */}
          {columns.length === 0 || rowData.length === 0 ? (
            <div className="dc-reporting-empty">
              {/* placeholder if we want later */}
            </div>
          ) : (
            <>
              {(!isPreviewExpanded || isPreview) && (
                <div
                  className="dc-reporting-grid"
                  style={{
                    gridTemplateColumns: `repeat(${columns.filter((column) => showLabel("employee-record", column)).length}, 1fr)`,
                  }}>
                  {columns
                    .filter((column) => showLabel("employee-record", column))
                    .map((column, index) => {
                      return (
                        <div key={index} className="dc-reporting-grid-header">
                          {getLabel("employee-record", column)}
                        </div>
                      );
                    })}
                  {rowData
                    .slice(
                      0,
                      !isPreviewExpanded && reportData.length > 5
                        ? 5
                        : rowData.length
                    )
                    .map((rowMap, rowIndex) => {
                      return (
                        <React.Fragment key={rowIndex}>
                          {columns
                            .filter((column) =>
                              showLabel("employee-record", column)
                            )
                            .map((column, colIndex) => {
                              let cellData = rowMap.get(column);
                              if (typeof cellData === "object") {
                                cellData = JSON.stringify(cellData);
                              }
                              return (
                                <div
                                  key={colIndex}
                                  className="dc-reporting-grid-item">
                                  {cellData}
                                </div>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                </div>
              )}

              {isPreviewExpanded && !isPreview && (
                <div className="report-preview-content">
                  <div
                    className="dc-reporting-grid expanded-grid"
                    style={{
                      gridTemplateColumns: `repeat(${columns.filter((column) => showLabel("employee-record", column)).length}, 1fr)`,
                    }}>
                    {columns
                      .filter((column) => showLabel("employee-record", column))
                      .map((column, index) => {
                        return (
                          <div key={index} className="dc-reporting-grid-header">
                            {getLabel("employee-record", column)}
                          </div>
                        );
                      })}
                    {rowData.map((rowMap, rowIndex) => {
                      return (
                        <React.Fragment key={rowIndex}>
                          {columns
                            .filter((column) =>
                              showLabel("employee-record", column)
                            )
                            .map((column, colIndex) => {
                              let cellData = rowMap.get(column);
                              if (typeof cellData === "object") {
                                cellData = JSON.stringify(cellData);
                              }
                              return (
                                <div
                                  key={colIndex}
                                  className="dc-reporting-grid-item">
                                  {cellData}
                                </div>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
